<template>
  <div class="socio-membresia">
    <!-- Detalle Membresía Card -->
    <div class="card main-card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="title mb-0">
          <i class="fa fa-credit-card mr-2 text-primary"></i>
          Detalle de la Membresía
        </h5>
        <div class="d-flex align-items-center">
          <span class="badge badge-lg mr-3" :class="getEstadoBadgeClass(estadoPagoGlosa)">
            <i class="fa fa-circle mr-1"></i>
            {{ estadoPagoGlosa }}
          </span>
          <el-select
            class="select-estado"
            size="small"
            v-model="estadoPagoModel"
            placeholder="Cambiar estado"
            :disabled="membershipReadOnly"
          >
            <el-option
              v-for="item in catalogMembresias.estadosPago"
              :key="item._id"
              :label="item.glosa"
              :value="item.glosa"
            >
              <span class="d-flex align-items-center">
                <i class="fa fa-circle mr-2" :class="getEstadoIconClass(item.glosa)"></i>
                {{ item.glosa }}
              </span>
            </el-option>
          </el-select>
        </div>
      </div>
      <div class="card-body">
        <form>
          <!-- Sección de Información del Plan -->
          <div class="section-card mb-4">
            <div class="section-header">
              <h6 class="section-title">
                <i class="fa fa-th-large mr-2"></i>
                Información del Plan
              </h6>
            </div>
            <div class="section-body">
              <!-- Advertencia si hay datos que no coinciden con catálogos -->
              <div 
                v-if="!membershipReadOnly && hasObsoleteCatalogData"
                class="alert alert-warning mb-3"
                role="alert"
              >
                <i class="fa fa-exclamation-triangle mr-2"></i>
                <strong>Nota:</strong> Algunos valores actuales no están disponibles en los catálogos. 
                Puedes mantenerlos sin cambios o seleccionar nuevos valores.
              </div>
              <div class="row">
                <div class="col-12 col-md-6 col-lg-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-folder-open mr-1 text-muted"></i>
                      Familia
                    </label>
                    <el-select
                      v-if="!membershipReadOnly"
                      v-model="selectedFamiliaId"
                      :placeholder="placeholderFamilia"
                      class="w-100"
                      @change="handleFamiliaChange"
                      clearable
                    >
                      <el-option
                        v-for="familia in catalogFamilias"
                        :key="familia._id"
                        :label="familia.nombre"
                        :value="familia.id"
                      >
                        <span class="d-flex align-items-center">
                          <i class="fa fa-folder-open mr-2"></i>
                          {{ familia.nombre }}
                        </span>
                      </el-option>
                    </el-select>
                    <input
                      v-else
                      type="text"
                      class="form-control form-control-modern"
                      placeholder="Sin membresía"
                      v-model.trim="localMembership.nombreFamilia"
                      :readonly="true"
                    />
                  </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-folder mr-1 text-muted"></i>
                      Sub Familia
                    </label>
                    <el-select
                      v-if="!membershipReadOnly"
                      v-model="selectedSubFamiliaId"
                      :placeholder="placeholderSubFamilia"
                      class="w-100"
                      @change="handleSubFamiliaChange"
                      clearable
                    >
                      <el-option
                        v-for="subfamilia in subfamiliasFiltradasPorFamilia"
                        :key="subfamilia._id"
                        :label="subfamilia.nombre"
                        :value="subfamilia.id"
                      >
                        <span class="d-flex align-items-center">
                          <i class="fa fa-folder mr-2"></i>
                          {{ subfamilia.nombre }}
                          <small class="ml-2 text-muted">({{ subfamilia.familia.nombre }})</small>
                        </span>
                      </el-option>
                    </el-select>
                    <input
                      v-else
                      type="text"
                      class="form-control form-control-modern"
                      placeholder="Sin membresía"
                      v-model.trim="localMembership.nombreSubFamilia"
                      :readonly="true"
                    />
                  </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-clock mr-1 text-muted"></i>
                      Turno
                    </label>
                    <el-select
                      v-if="!membershipReadOnly"
                      v-model="selectedTurnoId"
                      :placeholder="placeholderTurno"
                      class="w-100"
                      @change="handleTurnoChange"
                      clearable
                    >
                      <el-option
                        v-for="turno in catalogTurnos"
                        :key="turno._id"
                        :label="turno.descripcionTurno"
                        :value="turno.id"
                      >
                        <span class="d-flex align-items-center">
                          <i class="fa fa-clock mr-2"></i>
                          {{ turno.descripcionTurno }}
                          <span class="ml-2 badge badge-sm badge-success">{{ turno.totalDiasActivos }} días</span>
                        </span>
                      </el-option>
                    </el-select>
                    <div v-else class="input-group">
                      <input
                        type="text"
                        class="form-control form-control-modern"
                        placeholder="Sin membresía"
                        v-model="localMembership.turno.descripcionTurno"
                        :readonly="true"
                      />
                      <div class="input-group-append" v-if="localMembership.turno.descripcionTurno">
                        <el-tooltip :open-delay="200" placement="top">
                          <template #content>
                            Ver detalle del turno
                          </template>
                          <button
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="$emit('show-shift-modal')"
                          >
                            <i class="fa fa-eye"></i>
                          </button>
                        </el-tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sección de Configuración del Plan -->
          <div class="section-card mb-4">
            <div class="section-header">
              <h6 class="section-title">
                <i class="fa fa-cogs mr-2"></i>
                Configuración del Plan
              </h6>
            </div>
            <div class="section-body">
              <div class="row">
                <div class="col-12 col-md-6 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-tag mr-1 text-muted"></i>
                      Plan
                    </label>
                    <input
                      type="text"
                      class="form-control form-control-modern"
                      placeholder="Sin membresía"
                      v-model.trim="localMembership.descripcionPlan"
                      :readonly="membershipReadOnly"
                    />
                  </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-handshake mr-1 text-muted"></i>
                      Tipo de Convenio
                    </label>
                    <el-select
                      v-if="!membershipReadOnly"
                      v-model="localMembership.convenio"
                      placeholder="Seleccionar convenio"
                      class="w-100"
                    >
                      <el-option
                        v-for="conv in catalogMembresias.convenios"
                        :key="conv._id"
                        :label="conv.glosa"
                        :value="conv.glosa"
                      >
                        <span class="d-flex align-items-center">
                          <i class="fa mr-2" :class="conv.glosa === 'CONVENIO' ? 'fa-handshake text-primary' : 'fa-user text-secondary'"></i>
                          {{ conv.glosa }}
                        </span>
                      </el-option>
                    </el-select>
                    <input
                      v-else
                      type="text"
                      class="form-control form-control-modern"
                      :value="localMembership.convenio"
                      readonly
                    />
                  </div>
                </div>
              </div>
              
              <div class="row">
                <div class="col-12 col-md-6 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-building mr-1 text-muted"></i>
                      Sucursal Asignación
                    </label>
                    <input
                      type="text"
                      class="form-control form-control-modern"
                      placeholder="Sin membresía"
                      v-model="localMembership.nombreSucursalAsignacion"
                      :readonly="true"
                    />
                    <small class="text-muted">
                      <i class="fa fa-info-circle mr-1"></i>
                      La sucursal no puede cambiarse desde aquí
                    </small>
                  </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-layer-group mr-1 text-muted"></i>
                      Tipo de Plan
                    </label>
                    <el-select
                      v-if="!membershipReadOnly"
                      v-model="localMembership.tipoPlan"
                      placeholder="Seleccionar tipo"
                      class="w-100"
                    >
                      <el-option label="PRINCIPAL" value="PRINCIPAL">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-star mr-2 text-warning"></i>
                          PRINCIPAL
                        </span>
                      </el-option>
                      <el-option label="SECUNDARIO" value="SECUNDARIO">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-star-half-alt mr-2 text-info"></i>
                          SECUNDARIO
                        </span>
                      </el-option>
                    </el-select>
                    <input
                      v-else
                      type="text"
                      class="form-control form-control-modern"
                      :value="localMembership.tipoPlan || 'PRINCIPAL'"
                      readonly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Sección de Duración y Descuentos -->
          <div class="section-card mb-4">
            <div class="section-header">
              <h6 class="section-title">
                <i class="fa fa-calendar-alt mr-2"></i>
                Duración y Descuentos
              </h6>
            </div>
            <div class="section-body">
              <div class="row">
                <div class="col-12 col-md-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-calendar-alt mr-1 text-muted"></i>
                      Duración
                    </label>
                    <div class="input-group">
                      <input
                        type="number"
                        id="duracionPlan"
                        placeholder="0"
                        class="form-control form-control-modern"
                        min="1"
                        @input="validatePositive"
                        v-model.number="localMembership.duracionPlan"
                        :readonly="membershipReadOnly"
                      />
                      <div class="input-group-append">
                        <el-select
                          v-model="localMembership.unidadDuracionPlan"
                          placeholder="Unidad"
                          class="select-modern"
                          :disabled="membershipReadOnly"
                        >
                          <el-option
                            v-for="cat in catalogMembresias.unidadesDuracion"
                            :key="cat._id"
                            :label="cat.glosa"
                            :value="cat.glosa"
                          ></el-option>
                        </el-select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-percentage mr-1 text-muted"></i>
                      Descuento
                    </label>
                    <el-select
                      v-model="selectedDiscount"
                      placeholder="Selecciona %"
                      class="w-100"
                      :disabled="membershipReadOnly"
                    >
                      <el-option
                        v-for="(item, index) in discounts"
                        :key="index"
                        :value="item"
                      >{{ item }}%</el-option>
                    </el-select>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-snowflake mr-1 text-muted"></i>
                      Días Permitido Congelar
                    </label>
                    <input
                      type="number"
                      id="diasConelar"
                      placeholder="0"
                      class="form-control form-control-modern"
                      min="0"
                      @input="validatePositive"
                      v-model.number="localMembership.diasPermitidoCongelar"
                      :readonly="membershipReadOnly"
                    />
                  </div>
                </div>
              </div>
              
              <div class="row">
                <div class="col-12 col-md-4 mb-3">
                  <div class="custom-switch-container">
                    <label class="switch-label">
                      <i class="fa fa-map-marker-alt mr-1"></i>
                      Multi-sede
                    </label>
                    <p-switch
                      v-model="localMembership.informacionAdicional.multiSede"
                      type="success"
                      on-text="SI"
                      off-text="NO"
                      :readonly="membershipReadOnly"
                    ></p-switch>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Sección de Valores y Pagos -->
          <div class="section-card mb-4">
            <div class="section-header">
              <h6 class="section-title">
                <i class="fa fa-dollar-sign mr-2"></i>
                Valores y Pagos
              </h6>
            </div>
            <div class="section-body">
              <div class="row">
                <div class="col-12 col-md-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-money-bill-wave mr-1 text-muted"></i>
                      Valor del Plan
                    </label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text">$</span>
                      </div>
                      <input
                        id="valorPlan"
                        type="number"
                        class="form-control form-control-modern"
                        placeholder="0"
                        min="0"
                        @input="validatePositive"
                        v-model.number="localMembership.valorPlan"
                        :readonly="membershipReadOnly"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-receipt mr-1 text-muted"></i>
                      IVA
                    </label>
                    <el-select
                      v-model="localMembership.tipoIVA"
                      placeholder="Selecciona"
                      class="w-100"
                      :disabled="membershipReadOnly"
                    >
                      <el-option label="EXENTO" value="EXENTO">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-times-circle mr-2 text-danger"></i>
                          EXENTO
                        </span>
                      </el-option>
                      <el-option label="AFECTO" value="AFECTO">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-check-circle mr-2 text-success"></i>
                          AFECTO
                        </span>
                      </el-option>
                    </el-select>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="form-group">
                    <label class="form-label">
                      <i class="fa fa-calendar-check mr-1 text-muted"></i>
                      Último Pago
                    </label>
                    <el-date-picker
                      id="datePickerfechaUltimoPago"
                      type="date"
                      class="w-100"
                      placeholder="Seleccionar fecha"
                      format="dd-MM-yyyy"
                      value-format="dd-MM-yyyy"
                      v-model="localMembership.fechaUltimoPago"
                      :readonly="membershipReadOnly"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="row d-flex flex-row">
            <div class="d-flex flex-column col-6 col-xl-3 col-lg-4 col-md-4 d-md-none">
              <label for="datePickerfechaUltimoPago">Ultimo Pago</label>
              <el-date-picker
                id="datePickerfechaUltimoPago"
                type="date"
                label="Ultimo Pago"
                size="small"
                class="w-auto"
                placeholder="01-01-1900"
                format="dd-MM-yyyy"
                value-format="dd-MM-yyyy"
                v-model="localMembership.fechaUltimoPago"
                :readonly="membershipReadOnly"
              >
              </el-date-picker>
            </div>
            <div class="col-6 col-xl-3 col-lg-4 col-md-4 d-flex flex-column">
              <label for="datePickerfechaInicio">Fecha Inicio</label>
              <el-date-picker
                id="datePickerfechaInicio"
                type="date"
                size="small"
                class="w-auto"
                placeholder="01-01-1900"
                format="dd-MM-yyyy"
                value-format="dd-MM-yyyy"
                v-model="localMembership.fechaInicio"
                :readonly="membershipReadOnly"
              >
              </el-date-picker>
            </div>
            <div class="col-6 col-xl-3 col-lg-4 col-md-4 d-flex flex-column my-auto">
              <label for="datePickerfechaVencimiento">Fecha Vencimiento</label>
              <el-date-picker
                id="datePickerfechaVencimiento"
                type="date"
                placeholder="01-01-1900"
                format="dd-MM-yyyy"
                size="small"
                class="w-auto"
                value-format="dd-MM-yyyy"
                v-model="localMembership.fechaVencimiento"
                :readonly="membershipReadOnly"
              >
              </el-date-picker>
            </div>
            <div class="col-6 col-xl-3 col-lg-4 col-md-4">
              <label for="datePickerfechaAsignacion">Fecha Asignacion</label>
              <el-date-picker
                id="datePickerfechaAsignacion"
                type="date"
                placeholder="01-01-1900"
                size="small"
                class="w-auto"
                format="dd-MM-yyyy"
                value-format="dd-MM-yyyy"
                v-model="localMembership.fechaAsignacion"
                :readonly="membershipReadOnly"
              >
              </el-date-picker>
            </div>
          </div>
          
          <div class="container mt-2 d-flex justify-content-start flex-wrap flex-md-nowrap px-0">
            <!-- Sesiones -->
            <div
              class="container d-flex px-0 flex-row flex-md-column col-md-2"
              :class="{
                'col-12 px-md-auto': localMembership.indicadorUsoSesiones,
                'col-2': !localMembership.indicadorUsoSesiones,
              }"
            >
              <div class="switch d-flex flex-column">
                <label class="mx-auto">Sesiones</label>
                <p-switch
                  v-model="localMembership.indicadorUsoSesiones"
                  type="success"
                  class="my-auto mx-md-auto mr-2"
                  on-text="SI"
                  off-text="NO"
                  :readonly="membershipReadOnly"
                ></p-switch>
              </div>
              <div
                v-if="localMembership.indicadorUsoSesiones"
                class="row switch-body d-md-block justify-content-md-center"
              >
                <div class="text-center text-center col mb-0">
                  <label class="my-auto">Utilizadas</label>
                  <input
                    type="number"
                    @input="validatePositive"
                    id="sesionesUtilizadas"
                    placeholder="0"
                    class="form-control"
                    size="small"
                    v-model.number="localMembership.sesionesUtilizadas"
                    :readonly="membershipReadOnly"
                    min="0"
                  />
                </div>
                <div class="text-center text-center mt-0 col">
                  <label class="my-auto w-75">Max</label>
                  <input
                    class="form-control"
                    type="number"
                    @input="validatePositive"
                    id="sesionesLimite"
                    placeholder="0"
                    min="0"
                    size="small"
                    v-model.number="localMembership.sesionesLimite"
                    :readonly="membershipReadOnly"
                  />
                </div>
              </div>
            </div>
            
            <!-- Mes Calendario -->
            <div
              class="col-md-2 px-0"
              :class="{
                'col-8 px-0 px-md-auto d-flex flex-row flex-md-column': localMembership.indicadorMesCalendario,
                'col-3': !localMembership.indicadorMesCalendario,
              }"
            >
              <div class="switch d-flex flex-column px-4">
                <label class="mx-auto text-nowrap">Mes Calendario</label>
                <p-switch
                  v-model="localMembership.indicadorMesCalendario"
                  type="success"
                  class="mx-auto"
                  on-text="SI"
                  off-text="NO"
                  :readonly="membershipReadOnly"
                ></p-switch>
              </div>
              <div
                v-if="localMembership.indicadorMesCalendario"
                class="row switch-body d-flex justify-content-center"
              >
                <el-tooltip :open-delay="200" placement="top">
                  <template #content>
                    Esta opción te permite personalizar la duración del mes<br />
                    Por ejemplo, al seleccionar 30 días, <br />
                    todos los meses tendrán la misma duración de 30 días.
                  </template>
                  <div class="text-center text-center">
                    <label class="my-auto">Días del mes</label>
                    <input
                      @input="validatePositive"
                      id="diasMesNoCalendario"
                      type="number"
                      placeholder="0"
                      min="0"
                      size="small"
                      class="col-10 form-control mx-auto"
                      v-model.number="localMembership.diasMesNoCalendario"
                      :readonly="membershipReadOnly"
                    />
                  </div>
                </el-tooltip>
              </div>
            </div>
            
            <!-- Factura -->
            <div class="container col-auto col-md-2">
              <div class="switch d-flex flex-column">
                <label class="mx-auto">Factura</label>
                <p-switch
                  v-model="localMembership.indicadorFactura"
                  type="success"
                  class="mx-auto"
                  on-text="SI"
                  off-text="NO"
                  :readonly="membershipReadOnly"
                ></p-switch>
              </div>
            </div>
            
            <!-- Renovar -->
            <div
              class="px-0"
              :class="{
                'col-md-3 col-12 d-flex flex-row flex-md-column': localMembership.indicadorPermiteRenovar,
                'col-2 col-md-2': !localMembership.indicadorPermiteRenovar,
              }"
            >
              <div class="switch d-flex flex-column mr-2 mx-md-auto">
                <label class="mx-auto">Renovar</label>
                <p-switch
                  v-model="localMembership.indicadorPermiteRenovar"
                  type="success"
                  class="mx-auto mt-md-auto"
                  on-text="SI"
                  off-text="NO"
                  :readonly="membershipReadOnly"
                ></p-switch>
              </div>
              <div
                v-if="localMembership.indicadorPermiteRenovar"
                class="mt-0 text-center d-flex flex-row align-items-bottom"
              >
                <div
                  v-if="localMembership.unidadExpiracionRenovacion !== 'INDEFINIDA (I)'"
                >
                  <label for="expiracionRenovacion" class="">Cant</label>
                  <input
                    @input="validatePositive"
                    id="expiracionRenovacion"
                    type="number"
                    label="Cantidad"
                    min="0"
                    size="small"
                    v-model.number="localMembership.expiracionRenovacion"
                    class="form-control"
                    placeholder=""
                    :readonly="membershipReadOnly"
                  />
                </div>
                <div class="px-0 mx-0 d-flex flex-column">
                  <label for="unidadExpiracionRenovacion" class="mx-auto">Unidad</label>
                  <el-select
                    id="unidadExpiracionRenovacion"
                    v-model="localMembership.unidadExpiracionRenovacion"
                    placeholder="Selecciona"
                    class="success"
                    :class="localMembership.unidadExpiracionRenovacion === 'INDEFINIDA (I)' ? 'w-75 mx-auto' : ''"
                    size="small"
                    :disabled="membershipReadOnly"
                  >
                    <el-option
                      v-for="cat in catalogMembresias.unidadesExpiracionRenovacion"
                      :key="cat._id"
                      :label="cat.glosa"
                      :value="cat.glosa"
                    ></el-option>
                  </el-select>
                </div>
              </div>
            </div>

            <!-- Matricula -->
            <div
              class="col-md-2 px-0"
              :class="{
                'col-12 col-md-2 d-flex flex-row flex-md-column': localMembership.indicadorMatricula,
                'col-3 col-md-2': !localMembership.indicadorMatricula,
              }"
            >
              <div class="switch d-flex flex-column">
                <label class="mx-auto">Matricula</label>
                <p-switch
                  v-model="localMembership.indicadorMatricula"
                  type="success"
                  class="mx-auto"
                  on-text="SI"
                  off-text="NO"
                  :readonly="membershipReadOnly"
                ></p-switch>
              </div>
              <div
                v-if="localMembership.indicadorMatricula"
                class="switch-body d-md-block d-flex flex-row"
              >
                <div class="text-center col-6 col-md">
                  <label class="mb-0 mr-2">Valor</label>
                  <input
                    @input="validatePositive"
                    id="valorMatricula"
                    type="number"
                    placeholder="$0"
                    min="0"
                    class="form-control"
                    size="small"
                    v-model.number="localMembership.valorMatricula"
                    :readonly="membershipReadOnly"
                  />
                </div>
                <div class="text-center col">
                  <label class="mb-0 mr-2">Fecha</label>
                  <p>{{ localMembership.fechaPagoMatricula || "dd-MM-aaaa" }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Mensaje para socios tipo CARGA -->
          <div
            v-if="socio && socio.perfilCuenta === 'CARGA'"
            class="col-12 text-danger mt-4 d-flex flex-column"
          >
            <small class="font-weight-bold mx-auto text-center">
              {{
                socio.informacionAdicional && socio.informacionAdicional.rutTitularConvenio
                  ? `ESTE ES UN SOCIO DE TIPO [CARGA], CAMBIA SU TIPO EN "EDITAR SOCIO" O VE A LA MEMBRESÍA DEL TITULAR: `
                  : `ESTE ES UN SOCIO DE TIPO [CARGA], CAMBIA SU TIPO O ASIGNALO A UN CONVENIO EN "EDITAR SOCIO"`
              }}
            </small>
            <div
              class="w-auto btn mx-auto btn-sm btn-warning"
              @click="$emit('go-to-titular', socio.informacionAdicional.rutTitularConvenio)"
              v-if="socio && socio.informacionAdicional && socio.informacionAdicional.rutTitularConvenio"
            >
              {{ socio.informacionAdicional.rutTitularConvenio }}
            </div>
            <el-tooltip
              v-if="activeMembershipID"
              :open-delay="200"
              placement="top"
              class="d-none d-md-block"
            >
              <template #content>
                Eliminar Membresía, el sistema no toma en cuenta las
                membresías asignadas a socios de tipo [CARGA]
              </template>
              <button
                v-if="activeMembershipID"
                type="button"
                class="btn btn-outline-danger rounded-circle px-2 mx-auto"
                style="height: 50px; width: 50px; right: 20px; bottom: 10px;"
                @click="deleteMembership()"
              >
                <i class="fa fa-trash fa-2x ml-0 pl-0" aria-hidden="true"></i>
              </button>
            </el-tooltip>
          </div>
          
          <!-- Botones de acción -->
          <div
            v-else
            class="text-center my-2 mt-md-4 d-flex justify-content-center align-items-center position-relative"
          >
            <div class="d-flex justify-content-center">
              <button
                v-if="!activeMembershipID"
                type=""
                class="btn btn-info btn-fill btn-wd rounded-pill"
                @click.prevent="$emit('assign-membership')"
              >
                asignar membresía
              </button>
              <button
                v-if="membershipReadOnly && activeMembershipID"
                type=""
                class="btn btn-info btn-fill btn-wd"
                @click.prevent="enableMembershipEdit()"
              >
                editar
              </button>
              <button
                v-if="!membershipReadOnly && activeMembershipID"
                type="submit"
                class="btn btn-success btn-fill btn-wd"
                @click.prevent="saveMembershipChanges()"
              >
                guardar
              </button>
              <button
                v-if="!membershipReadOnly && activeMembershipID"
                type="submit"
                class="btn btn-danger btn-fill btn-wd ml-4"
                @click.prevent="cancelMembershipEdit()"
              >
                cancelar
              </button>
              <el-tooltip
                v-if="!membershipReadOnly && activeMembershipID"
                :open-delay="200"
                placement="top"
                class="d-none d-md-inline-block ml-4"
              >
                <template #content> Eliminar Membresía </template>
                <button
                  type="button"
                  class="btn btn-outline-danger rounded-circle"
                  style="height: 50px; width: 50px;"
                  @click="deleteMembership()"
                >
                  <i class="fa fa-trash fa-lg" aria-hidden="true"></i>
                </button>
              </el-tooltip>
            </div>
          </div>
          
          <!-- Botón eliminar para móvil -->
          <div class="col-12 d-flex d-md-none" v-if="!membershipReadOnly && activeMembershipID">
            <el-tooltip
              :open-delay="200"
              placement="top"
            >
              <template #content> Eliminar Membresía </template>
              <button
                type="button"
                class="btn btn-outline-danger rounded-circle mx-auto"
                style="height: 50px; width: 50px;"
                @click="deleteMembership()"
              >
                <i class="fa fa-trash fa-lg" aria-hidden="true"></i>
              </button>
            </el-tooltip>
          </div>
          
          <!-- Información de control para soporte -->
          <div class="col-12 mt-4 pt-3 border-top" v-if="localMembership._id">
            <div class="control-info">
              <small class="text-muted d-block mb-1">
                <i class="fa fa-info-circle mr-1"></i>
                <strong>Información de Control (Soporte Técnico)</strong>
              </small>
              <div class="d-flex align-items-center">
                <code class="bg-light px-2 py-1 rounded border">
                  ID Membresía: {{ localMembership._id }}
                </code>
                <el-tooltip content="Copiar ID" placement="top">
                  <button 
                    type="button"
                    class="btn btn-sm btn-outline-secondary ml-2"
                    @click="copyMembershipId"
                  >
                    <i class="fa fa-copy"></i>
                  </button>
                </el-tooltip>
              </div>
            </div>
          </div>
          
          <div class="clearfix"></div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import PSwitch from "src/components/UIComponents/Switch.vue";
import { Tooltip, Select, Option, DatePicker } from "element-ui";
import _ from "lodash";

export default {
  name: 'SocioMembresia',
  components: {
    PSwitch,
    [Tooltip.name]: Tooltip,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },
  props: {
    membership: {
      type: Object,
      default: () => ({})
    },
    socio: {
      type: Object,
      default: () => ({})
    },
    catalogFamilias: {
      type: Array,
      default: () => []
    },
    catalogSubfamilias: {
      type: Array,
      default: () => []
    },
    catalogTurnos: {
      type: Array,
      default: () => []
    },
    catalogMembresias: {
      type: Object,
      default: () => ({
        unidadesDuracion: [],
        convenios: [],
        unidadesExpiracionRenovacion: [],
        estadosPago: []
      })
    }
  },
  data() {
    return {
      localMembership: {},
      foundMembershipOG: {},
      membershipReadOnly: true,
      selectedDiscount: 0,
      discounts: [
        0, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
      ],
      filters: {
        indicadorPermiteCongelar: false
      },
      isLoaded: false,
      // IDs seleccionados para dropdowns
      selectedFamiliaId: null,
      selectedSubFamiliaId: null,
      selectedTurnoId: null
    };
  },
  computed: {
    activeMembershipID() {
      return this.localMembership._id || false;
    },
    estadoPagoGlosa() {
      // Extraer solo la glosa del estado de pago (puede venir como objeto o string)
      if (!this.localMembership.estadoPago) return 'SIN ESTADO';
      if (typeof this.localMembership.estadoPago === 'string') {
        return this.localMembership.estadoPago;
      }
      if (this.localMembership.estadoPago.glosa) {
        return this.localMembership.estadoPago.glosa;
      }
      return 'SIN ESTADO';
    },
    estadoPagoModel: {
      get() {
        return this.estadoPagoGlosa;
      },
      set(newValue) {
        this.localMembership.estadoPago = newValue;
      }
    },
    subfamiliasFiltradasPorFamilia() {
      if (!this.selectedFamiliaId) {
        return this.catalogSubfamilias;
      }
      return this.catalogSubfamilias.filter(sf => sf.familia.id === this.selectedFamiliaId);
    },
    placeholderFamilia() {
      return this.localMembership.nombreFamilia ? 'Actual: ' + this.localMembership.nombreFamilia : 'Seleccionar familia';
    },
    placeholderSubFamilia() {
      return this.localMembership.nombreSubFamilia ? 'Actual: ' + this.localMembership.nombreSubFamilia : 'Seleccionar subfamilia';
    },
    placeholderTurno() {
      if (this.localMembership.turno && this.localMembership.turno.descripcionTurno) {
        return 'Actual: ' + this.localMembership.turno.descripcionTurno;
      }
      return 'Seleccionar turno';
    },
    hasObsoleteCatalogData() {
      // Verificar si hay datos de la membresía que no están en los catálogos actuales
      let hasObsolete = false;
      
      if (this.localMembership.idFamilia && this.catalogFamilias.length > 0) {
        const familiaExists = this.catalogFamilias.find(f => f.id === this.localMembership.idFamilia);
        if (!familiaExists) hasObsolete = true;
      }
      
      if (this.localMembership.idSubFamilia && this.catalogSubfamilias.length > 0) {
        const subfamiliaExists = this.catalogSubfamilias.find(sf => sf.id === this.localMembership.idSubFamilia);
        if (!subfamiliaExists) hasObsolete = true;
      }
      
      if (this.localMembership.turno && this.localMembership.turno.id && this.catalogTurnos.length > 0) {
        const turnoExists = this.catalogTurnos.find(t => t.id === this.localMembership.turno.id);
        if (!turnoExists) hasObsolete = true;
      }
      
      return hasObsolete;
    }
  },
  watch: {
    membership: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.localMembership = _.cloneDeep(newVal);
          this.foundMembershipOG = _.cloneDeep(newVal);
          
          // Normalizar campos que pueden venir como objetos del backend
          if (this.localMembership.estadoPago && typeof this.localMembership.estadoPago === 'object' && this.localMembership.estadoPago.glosa) {
            this.localMembership.estadoPago = this.localMembership.estadoPago.glosa;
          }
          
          if (this.localMembership.unidadDuracionPlan && typeof this.localMembership.unidadDuracionPlan === 'object' && this.localMembership.unidadDuracionPlan.glosa) {
            this.localMembership.unidadDuracionPlan = this.localMembership.unidadDuracionPlan.glosa;
          }
          
          if (this.localMembership.unidadExpiracionRenovacion && typeof this.localMembership.unidadExpiracionRenovacion === 'object' && this.localMembership.unidadExpiracionRenovacion.glosa) {
            this.localMembership.unidadExpiracionRenovacion = this.localMembership.unidadExpiracionRenovacion.glosa;
          }
          
          if (this.localMembership.convenio && typeof this.localMembership.convenio === 'object' && this.localMembership.convenio.glosa) {
            this.localMembership.convenio = this.localMembership.convenio.glosa;
          }
          
          // Asegurar que tipoPlan esté seteado (por defecto PRINCIPAL)
          if (!this.localMembership.tipoPlan) {
            this.localMembership.tipoPlan = "PRINCIPAL";
          }
          
          // Asegurar que turno tenga idTurno si tiene id
          if (this.localMembership.turno && this.localMembership.turno.id && !this.localMembership.turno.idTurno) {
            this.localMembership.turno.idTurno = this.localMembership.turno.id;
          }
          
          // Set selected discount
          if (this.localMembership.porcentajeDescuento) {
            this.selectedDiscount = this.localMembership.porcentajeDescuento * 100;
          }
          
          // Set filters
          this.filters.indicadorPermiteCongelar = this.localMembership.diasPermitidoCongelar > 0;
          
          // Set IDs seleccionados SOLO si existen en los catálogos actuales
          this.selectedFamiliaId = null;
          this.selectedSubFamiliaId = null;
          this.selectedTurnoId = null;
          
          // Verificar si la familia existe en el catálogo
          if (this.localMembership.idFamilia && this.catalogFamilias.length > 0) {
            const familiaExists = this.catalogFamilias.find(f => f.id === this.localMembership.idFamilia);
            if (familiaExists) {
              this.selectedFamiliaId = this.localMembership.idFamilia;
            }
          }
          
          // Verificar si la subfamilia existe en el catálogo
          if (this.localMembership.idSubFamilia && this.catalogSubfamilias.length > 0) {
            const subfamiliaExists = this.catalogSubfamilias.find(sf => sf.id === this.localMembership.idSubFamilia);
            if (subfamiliaExists) {
              this.selectedSubFamiliaId = this.localMembership.idSubFamilia;
            }
          }
          
          // Verificar si el turno existe en el catálogo
          if (this.localMembership.turno && this.localMembership.turno.id && this.catalogTurnos.length > 0) {
            const turnoExists = this.catalogTurnos.find(t => t.id === this.localMembership.turno.id);
            if (turnoExists) {
              this.selectedTurnoId = this.localMembership.turno.id;
            }
          }
          
          this.isLoaded = true;
        }
      }
    },
    selectedDiscount(newValue) {
      if (this.isLoaded) {
        this.localMembership.porcentajeDescuento = newValue / 100;
      }
    },
    "localMembership.diasPermitidoCongelar"(newValue) {
      const value = parseInt(newValue);
      if (this.isLoaded) {
        if (!value) this.filters.indicadorPermiteCongelar = false;
      }
    },
    "filters.indicadorPermiteCongelar"(newValue) {
      if (this.isLoaded) {
        if (newValue) {
          this.localMembership.diasPermitidoCongelar = 1;
        } else {
          this.localMembership.diasPermitidoCongelar = 0;
        }
      }
    },
    "localMembership.indicadorMesCalendario"(newValue, oldValue) {
      if (this.isLoaded) {
        if (!newValue && newValue !== oldValue) {
          this.localMembership.indicadorUsoSesiones = true;
          this.localMembership.sesionesLimite = this.localMembership.sesionesLimite || 1;
          this.localMembership.sesionesUtilizadas = 0;
          this.localMembership.diasMesNoCalendario = 0;
        } else if (newValue && newValue !== oldValue) {
          this.localMembership.diasMesNoCalendario = 0;
        }
      }
    },
    "localMembership.indicadorUsoSesiones"(newValue, oldValue) {
      if (this.isLoaded) {
        if (newValue && !oldValue) {
          this.localMembership.sesionesLimite = this.localMembership.sesionesLimite || 1;
          this.localMembership.sesionesUtilizadas = 0;
        } else if (!newValue && oldValue) {
          this.localMembership.sesionesLimite = 0;
        }
      }
    },
    "localMembership.fechaVencimiento"(newValue) {
      if (newValue === null) {
        this.localMembership.fechaVigencia = "";
        this.localMembership.fechaVencimiento = "";
      } else {
        this.localMembership.fechaVigencia = newValue;
      }
    },
    "localMembership.fechaInicio"(newValue) {
      if (newValue === null) {
        this.localMembership.fechaInicio = "";
      }
    },
    // Watchers para catálogos - verificar matches cuando se cargan
    catalogFamilias(newVal) {
      if (newVal && newVal.length > 0 && this.localMembership.idFamilia) {
        const familiaExists = newVal.find(f => f.id === this.localMembership.idFamilia);
        this.selectedFamiliaId = familiaExists ? this.localMembership.idFamilia : null;
      }
    },
    catalogSubfamilias(newVal) {
      if (newVal && newVal.length > 0 && this.localMembership.idSubFamilia) {
        const subfamiliaExists = newVal.find(sf => sf.id === this.localMembership.idSubFamilia);
        this.selectedSubFamiliaId = subfamiliaExists ? this.localMembership.idSubFamilia : null;
      }
    },
    catalogTurnos(newVal) {
      if (newVal && newVal.length > 0 && this.localMembership.turno && this.localMembership.turno.id) {
        const turnoExists = newVal.find(t => t.id === this.localMembership.turno.id);
        this.selectedTurnoId = turnoExists ? this.localMembership.turno.id : null;
      }
    }
  },
  methods: {
    getEstadoBadgeClass(estado) {
      const estadoClasses = {
        'VIGENTE': 'badge-success',
        'PAGADO': 'badge-success',
        'PENDIENTE': 'badge-warning',
        'R:PENDIENTE': 'badge-warning',
        'VENCIDO': 'badge-danger',
        'CONGELADO': 'badge-info',
        'FINALIZADO': 'badge-secondary',
        'CANCELADO': 'badge-dark',
        'ANULADO': 'badge-dark'
      };
      return estadoClasses[estado] || 'badge-secondary';
    },
    
    getEstadoIconClass(estado) {
      const estadoClasses = {
        'VIGENTE': 'text-success',
        'PAGADO': 'text-success',
        'PENDIENTE': 'text-warning',
        'R:PENDIENTE': 'text-warning',
        'VENCIDO': 'text-danger',
        'CONGELADO': 'text-info',
        'FINALIZADO': 'text-secondary',
        'CANCELADO': 'text-dark',
        'ANULADO': 'text-dark'
      };
      return estadoClasses[estado] || 'text-secondary';
    },
    
    validatePositive(event) {
      const inputElement = event.target;
      const inputValue = parseFloat(inputElement.value);
      if (inputValue < 0) {
        inputElement.value = 0;
      }
    },
    
    handleFamiliaChange(familiaId) {
      const familia = this.catalogFamilias.find(f => f.id === familiaId);
      if (familia) {
        this.localMembership.nombreFamilia = familia.nombre;
        this.localMembership.idFamilia = familia.id;
        // Resetear subfamilia cuando cambia la familia
        this.selectedSubFamiliaId = null;
        this.localMembership.nombreSubFamilia = "";
        this.localMembership.idSubFamilia = null;
      }
    },
    
    handleSubFamiliaChange(subfamiliaId) {
      const subfamilia = this.catalogSubfamilias.find(sf => sf.id === subfamiliaId);
      if (subfamilia) {
        this.localMembership.nombreSubFamilia = subfamilia.nombre;
        this.localMembership.idSubFamilia = subfamilia.id;
        // También actualizar la familia si es necesario
        this.selectedFamiliaId = subfamilia.familia.id;
        this.localMembership.nombreFamilia = subfamilia.familia.nombre;
        this.localMembership.idFamilia = subfamilia.familia.id;
      }
    },
    
    handleTurnoChange(turnoId) {
      const turno = this.catalogTurnos.find(t => t.id === turnoId);
      if (turno) {
        // Actualizar el objeto turno completo con idTurno para el endpoint
        this.localMembership.turno = {
          ...turno,
          id: turno.id,
          idTurno: turno.id,  // Agregar idTurno para el endpoint de actualización
          descripcionTurno: turno.descripcionTurno
        };
      }
    },
    
    enableMembershipEdit() {
      if (!this.activeMembershipID) {
        this.$swal.fire({
          title: "ERROR! :(",
          text: "No encuentro una membresía para modificar",
          icon: "error",
          confirmButtonColor: "#eb6532",
        });
        return;
      }
      this.foundMembershipOG = _.cloneDeep(this.localMembership);
      this.membershipReadOnly = false;
    },
    
    cancelMembershipEdit() {
      this.localMembership = _.cloneDeep(this.foundMembershipOG);
      this.membershipReadOnly = true;
      
      // Restaurar IDs seleccionados a los valores originales (si existen en catálogos)
      this.selectedFamiliaId = null;
      this.selectedSubFamiliaId = null;
      this.selectedTurnoId = null;
      
      if (this.localMembership.idFamilia && this.catalogFamilias.length > 0) {
        const familiaExists = this.catalogFamilias.find(f => f.id === this.localMembership.idFamilia);
        if (familiaExists) {
          this.selectedFamiliaId = this.localMembership.idFamilia;
        }
      }
      
      if (this.localMembership.idSubFamilia && this.catalogSubfamilias.length > 0) {
        const subfamiliaExists = this.catalogSubfamilias.find(sf => sf.id === this.localMembership.idSubFamilia);
        if (subfamiliaExists) {
          this.selectedSubFamiliaId = this.localMembership.idSubFamilia;
        }
      }
      
      if (this.localMembership.turno && this.localMembership.turno.id && this.catalogTurnos.length > 0) {
        const turnoExists = this.catalogTurnos.find(t => t.id === this.localMembership.turno.id);
        if (turnoExists) {
          this.selectedTurnoId = this.localMembership.turno.id;
        }
      }
    },
    
    async saveMembershipChanges() {
      let updatedFields = {};
      let shiftWasUpdated = false;
      
      try {
        Object.keys(this.localMembership).forEach((key) => {
          if (!_.isEqual(this.localMembership[key], this.foundMembershipOG[key])) {
            if (key === "turno") {
              shiftWasUpdated = true;
            }
            updatedFields[key] = this.localMembership[key];
          }
        });
        
        if (updatedFields.fechaAsignacion) {
          const date = new Date(updatedFields.fechaAsignacion);
          updatedFields.fechaAsignacion = date.toISOString();
        }
      } catch (error) {
        console.error(error);
      }
      
      if (Object.keys(updatedFields).length > 0) {
        this.$swal.fire({
          title: "¿Estás seguro?",
          text: `Has modificado: ${Object.keys(updatedFields).join(",")}`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#6bd098",
          cancelButtonColor: "#eb6532",
          confirmButtonText: "Sí, guardar cambios",
          cancelButtonText: "Cancelar",
        }).then((result) => {
          if (result.isConfirmed) {
            this.updateMembership(updatedFields, shiftWasUpdated);
          } else {
            this.cancelMembershipEdit();
          }
        });
      } else {
        this.$swal.fire({
          title: "No hay Cambios",
          text: "No tienes cambios que guardar",
          icon: "info",
          confirmButtonText: "entendido",
          customClass: {
            confirmButton: "btn btn-success",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            this.cancelMembershipEdit();
          }
        });
      }
    },
    
    async updateMembership(updatedFields, updateShift) {
      this.$swal.fire({
        title: "Actualizando...",
        text: "Por favor espera",
        timerProgressBar: true,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      // Preparar el turno con el formato correcto
      let turnoData = null;
      if (this.localMembership.turno) {
        turnoData = {
          idTurno: this.localMembership.turno.idTurno || this.localMembership.turno.id,
          descripcionTurno: this.localMembership.turno.descripcionTurno,
          lunesAutorizado: this.localMembership.turno.lunesAutorizado || false,
          martesAutorizado: this.localMembership.turno.martesAutorizado || false,
          miercolesAutorizado: this.localMembership.turno.miercolesAutorizado || false,
          juevesAutorizado: this.localMembership.turno.juevesAutorizado || false,
          viernesAutorizado: this.localMembership.turno.viernesAutorizado || false,
          sabadoAutorizado: this.localMembership.turno.sabadoAutorizado || false,
          domingoAutorizado: this.localMembership.turno.domingoAutorizado || false,
          lunesHoraInicio: this.localMembership.turno.lunesHoraInicio || "00:00:00",
          lunesHoraTermino: this.localMembership.turno.lunesHoraTermino || "00:00:00",
          martesHoraInicio: this.localMembership.turno.martesHoraInicio || "00:00:00",
          martesHoraTermino: this.localMembership.turno.martesHoraTermino || "00:00:00",
          miercolesHoraInicio: this.localMembership.turno.miercolesHoraInicio || "00:00:00",
          miercolesHoraTermino: this.localMembership.turno.miercolesHoraTermino || "00:00:00",
          juevesHoraInicio: this.localMembership.turno.juevesHoraInicio || "00:00:00",
          juevesHoraTermino: this.localMembership.turno.juevesHoraTermino || "00:00:00",
          viernesHoraInicio: this.localMembership.turno.viernesHoraInicio || "00:00:00",
          viernesHoraTermino: this.localMembership.turno.viernesHoraTermino || "00:00:00",
          sabadoHoraInicio: this.localMembership.turno.sabadoHoraInicio || "00:00:00",
          sabadoHoraTermino: this.localMembership.turno.sabadoHoraTermino || "00:00:00",
          domingoHoraInicio: this.localMembership.turno.domingoHoraInicio || "00:00:00",
          domingoHoraTermino: this.localMembership.turno.domingoHoraTermino || "00:00:00"
        };
        
        // Normalizar horarios si se está actualizando el turno
        if (updateShift) {
          Object.keys(turnoData).forEach((key) => {
            if (/Hora/.test(key)) {
              let valor = turnoData[key];
              const formatoIncompleto = /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(valor);
              if (formatoIncompleto) {
                turnoData[key] = valor + ":00";
              }
            }
          });
        }
      }
      
      // Construir el Body SOLO con los campos que el endpoint espera
      const bodyData = {
        idFamilia: this.localMembership.idFamilia,
        nombreFamilia: this.localMembership.nombreFamilia,
        idSubFamilia: this.localMembership.idSubFamilia,
        nombreSubFamilia: this.localMembership.nombreSubFamilia,
        idPlan: this.localMembership.idPlan,
        descripcionPlan: this.localMembership.descripcionPlan,
        valorPlan: this.localMembership.valorPlan,
        tipoIVA: this.localMembership.tipoIVA,
        convenio: this.localMembership.convenio,
        indicadorMatricula: this.localMembership.indicadorMatricula || false,
        valorMatricula: this.localMembership.valorMatricula || 0,
        fechaPagoMatricula: this.localMembership.fechaPagoMatricula || "",
        indicadorFactura: this.localMembership.indicadorFactura || false,
        diasPermitidoCongelar: this.localMembership.diasPermitidoCongelar || 0,
        duracionPlan: this.localMembership.duracionPlan,
        unidadDuracionPlan: this.localMembership.unidadDuracionPlan,
        porcentajeDescuento: this.localMembership.porcentajeDescuento || 0,
        indicadorMesCalendario: this.localMembership.indicadorMesCalendario || false,
        diasMesNoCalendario: this.localMembership.diasMesNoCalendario || 0,
        indicadorUsoSesiones: this.localMembership.indicadorUsoSesiones || false,
        sesionesLimite: this.localMembership.sesionesLimite || 0,
        sesionesUtilizadas: this.localMembership.sesionesUtilizadas || 0,
        indicadorPermiteRenovar: this.localMembership.indicadorPermiteRenovar || false,
        expiracionRenovacion: this.localMembership.expiracionRenovacion || 0,
        unidadExpiracionRenovacion: this.localMembership.unidadExpiracionRenovacion || "",
        fechaAsignacion: this.localMembership.fechaAsignacion || "",
        fechaInicio: this.localMembership.fechaInicio || "",
        fechaVencimiento: this.localMembership.fechaVencimiento || "",
        fechaVigencia: this.localMembership.fechaVigencia || "",
        fechaUltimoPago: this.localMembership.fechaUltimoPago || "",
        horaUltimoPago: this.localMembership.horaUltimoPago || "",
        estadoPlan: this.localMembership.estadoPago || "PENDIENTE",
        indicadorActualizarTurno: updateShift || false,
        turno: turnoData,
        informacionAdicional: this.localMembership.informacionAdicional || { multiSede: false }
      };
      
      try {
        const result = await this.$MasterControlAPI.post(
          "",
          {
            Header: {
              Operacion: JSON.parse(sessionStorage.getItem("wxZlBbdD4q")).UpdateMembershipAPIv3,
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD"),
            },
            Parameters: {
              idSucursal: this.localMembership.sucursalAsignacion || sessionStorage.getItem("nu5za9cv6q"),
              nombreSucursal: this.localMembership.nombreSucursalAsignacion || sessionStorage.getItem("qtsb4ec3d8"),
              rut: this.socio.rut || this.localMembership.rutSocio,
              tipoPlan: this.localMembership.tipoPlan || "PRINCIPAL",
            },
            Body: bodyData
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        this.$swal.close();
        
        if (result.data.Response.Resultado.CodigoHTTP === 201 || result.data.Response.Resultado.CodigoHTTP === 200) {
          this.foundMembershipOG = this.localMembership;
          this.membershipReadOnly = true;
          
          this.$swal.fire({
            title: "OK!",
            text: "Haz modificado la membresía de forma exitosa",
            icon: "success",
            confirmButtonColor: "#6bd098",
            confirmButtonText: "ok",
          }).then(() => {
            this.$emit('membership-updated', this.localMembership);
          });
        } else {
          this.foundMembershipOG = this.localMembership;
          this.membershipReadOnly = true;
          
          this.$swal.fire({
            title: "ERROR!",
            text: result.data.Response.Resultado.MensajeInterno,
            icon: "warning",
            confirmButtonColor: "#eb6532",
            confirmButtonText: "ok",
          });
        }
      } catch (error) {
        this.$swal.close();
        
        this.$swal.fire({
          title: "ERROR!",
          text: error.message,
          icon: "warning",
          confirmButtonColor: "#eb6532",
          confirmButtonText: "ok",
        });
        console.error("Error: ", error.message);
      }
    },
    
    copyMembershipId() {
      const idMembresia = this.localMembership._id;
      if (idMembresia) {
        // Copiar al portapapeles
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(idMembresia).then(() => {
            this.$swal.fire({
              title: 'ID Copiado',
              text: 'El ID de la membresía ha sido copiado al portapapeles',
              icon: 'success',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 2000
            });
          }).catch(() => {
            this.fallbackCopyTextToClipboard(idMembresia);
          });
        } else {
          this.fallbackCopyTextToClipboard(idMembresia);
        }
      }
    },
    
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.$swal.fire({
          title: 'ID Copiado',
          text: 'El ID de la membresía ha sido copiado',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        console.error('Error al copiar:', err);
      }
      document.body.removeChild(textArea);
    },
    
    async deleteMembership() {
      const socioName = this.socio.nombreSocio || "el socio";
      const planName = this.localMembership.descripcionPlan || "la membresía";
      
      this.$swal.fire({
        title: `¿Eliminar membresía?`,
        html: `
          <p>Estás a punto de eliminar la membresía <strong>${planName}</strong> de <strong>${socioName}</strong>.</p>
          <p class="text-danger"><i class="fa fa-exclamation-triangle"></i> Esta acción no se puede deshacer.</p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#eb6532",
        cancelButtonColor: "#6bd098",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.$swal.fire({
            title: "Eliminando...",
            text: "Por favor espera",
            allowOutsideClick: false,
            didOpen: () => {
              this.$swal.showLoading();
            },
          });
          
          try {
            const response = await this.$MasterControlAPI.post(
              "",
              {
                Header: {
                  Operacion: "/v3/user/socios/eliminar/plan",
                },
                Auth: {
                  Token: sessionStorage.getItem("jCxbraoQeD"),
                },
                Parameters: {
                  rut: this.socio.rut || this.localMembership.rutSocio,
                  tipoPlan: "PRINCIPAL",
                  modalidadPlan: "ON-LINE",
                  descripcionPlan: this.localMembership.descripcionPlan,
                },
                Body: {},
              },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            
            if (response.data.Response.Resultado.CodigoHTTP === 200 || response.data.Response.Resultado.CodigoHTTP === 201) {
              this.$swal.fire({
                title: "¡Membresía Eliminada!",
                text: "La membresía ha sido eliminada exitosamente",
                icon: "success",
                confirmButtonColor: "#6bd098",
              }).then(() => {
                this.$emit('membership-deleted');
              });
            } else {
              throw new Error(response.data.Response.Resultado.MensajeInterno);
            }
          } catch (error) {
            this.$swal.fire({
              title: "Error al eliminar",
              text: error.message || "No se pudo eliminar la membresía",
              icon: "error",
              confirmButtonColor: "#eb6532",
            });
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.socio-membresia {
  width: 100%;
}

/* Card principal */
.socio-membresia .main-card {
  border: none;
  box-shadow: 0 0 20px rgba(0,0,0,0.08);
  margin-bottom: 0;
  background: #fff;
  border-radius: 0.75rem;
}

.socio-membresia .card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border-bottom: 2px solid #e9ecef;
  padding: 1.5rem;
  border-radius: 0.75rem 0.75rem 0 0;
}

.socio-membresia .card-header h5 {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 0;
  font-size: 1.1rem;
}

.socio-membresia .card-body {
  padding: 2rem;
}

/* Secciones del formulario */
.section-card {
  background: #f8f9fa;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.section-header {
  background: #fff;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.section-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
}

.section-title i {
  color: #667eea;
}

.section-body {
  padding: 1.5rem;
}

/* Labels y form groups */
.form-group {
  margin-bottom: 0;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-label i {
  font-size: 0.875rem;
}

/* Inputs modernos */
.form-control-modern {
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  padding: 0.625rem 1rem;
  transition: all 0.3s ease;
  background-color: #fff;
}

.form-control-modern:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.1);
  outline: none;
}

.form-control-modern[readonly] {
  background-color: #f8f9fa;
  cursor: not-allowed;
  border-color: #dee2e6;
  color: #6c757d;
}

/* Input groups mejorados */
.input-group {
  position: relative;
}

.input-group-append .btn {
  border: 2px solid #e9ecef;
  border-left: none;
  background: #fff;
  color: #6c757d;
  padding: 0.625rem 1rem;
  transition: all 0.3s ease;
}

.input-group-append .btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.input-group-text {
  border: 2px solid #e9ecef;
  border-left: none;
  background: #f8f9fa;
  color: #6c757d;
  font-weight: 500;
}

/* Select personalizado */
.select-estado {
  min-width: 150px;
}

.select-modern >>> .el-input__inner {
  border: 2px solid #e9ecef;
  border-radius: 0 0.5rem 0.5rem 0;
  border-left: none;
  font-size: 0.875rem;
  height: 42px;
}

/* Estados badge */
.badge-lg {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  font-weight: 500;
  border-radius: 2rem;
}

/* Switches personalizados */
.custom-switch-container {
  text-align: center;
  padding: 0.5rem;
}

.switch-label {
  font-size: 0.75rem;
  color: #6c757d;
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.switch {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cursor-pointer {
  cursor: pointer;
  transition: all 0.3s ease;
}

.cursor-pointer:hover {
  transform: scale(1.05);
  color: #667eea;
}

/* Override de form-control básico */
.form-control {
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  padding: 0.625rem 1rem;
  transition: all 0.3s ease;
}

.form-control[readonly] {
  background-color: #f8f9fa;
  cursor: not-allowed;
  border-color: #dee2e6;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.1);
}

.el-select {
  width: 100%;
}

.el-select >>> .el-input__inner {
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  height: 42px;
  line-height: 42px;
  padding: 0 1rem;
}

.el-select >>> .el-input__inner:focus {
  border-color: #667eea;
}

/* Placeholder con texto "Actual: X" */
.el-select >>> .el-input__inner::placeholder {
  color: #6c757d;
  font-style: italic;
  font-size: 0.875rem;
}

/* Alerta de datos obsoletos */
.alert {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  border-left: 4px solid #ffc107;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

/* Información de control para soporte */
.control-info {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}

.control-info code {
  font-size: 0.8125rem;
  color: #495057;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}

.control-info .btn {
  padding: 0.25rem 0.5rem;
}

/* Botones mejorados */
.btn-fill {
  min-width: 120px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.btn-success.btn-fill {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  box-shadow: 0 4px 6px rgba(40, 167, 69, 0.3);
}

.btn-success.btn-fill:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(40, 167, 69, 0.4);
}

.btn-secondary.btn-fill {
  background: #6c757d;
  border: none;
  box-shadow: 0 4px 6px rgba(108, 117, 125, 0.3);
}

.btn-secondary.btn-fill:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(108, 117, 125, 0.4);
}

.btn-outline-danger.rounded-circle {
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-width: 2px;
  vertical-align: middle;
}

.btn-outline-danger.rounded-circle:hover {
  background-color: #dc3545;
  color: white;
  transform: scale(1.05) rotate(15deg);
}

/* Date picker mejorado */
.el-date-picker {
  width: 100% !important;
}

.el-date-picker >>> .el-input__inner {
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  height: 42px;
  line-height: 42px;
  padding: 0 1rem;
}

.el-date-picker >>> .el-input__inner:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.1);
}

/* Iconos de acción */
.action-icon {
  font-size: 1.25rem;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 0.5rem;
}

.action-icon:hover {
  color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  transform: scale(1.1);
}

/* Mejoras para switches */
.p-switch >>> .switch-label {
  font-weight: 600;
}

/* Separadores visuales */
hr {
  border-color: #e9ecef;
  margin: 2rem 0;
}

/* Estados de solo lectura */
input[readonly],
.el-select.is-disabled >>> .el-input__inner,
.el-date-picker.is-disabled >>> .el-input__inner {
  background-color: #f8f9fa !important;
  cursor: not-allowed !important;
  opacity: 0.7;
}

/* Animaciones de hover para secciones */
.section-card {
  transform: translateY(0);
  transition: all 0.3s ease;
}

.section-card:hover {
  transform: translateY(-2px);
}

/* Responsive adjustments específicos */
@media (max-width: 768px) {
  .section-body {
    padding: 1rem;
  }
  
  .form-label {
    font-size: 0.75rem;
  }
  
  .section-title {
    font-size: 0.875rem;
  }
  
  .btn-fill {
    font-size: 0.8125rem;
    padding: 0.625rem 1rem;
  }
}

.el-select >>> .el-input__inner {
  font-size: 0.875rem;
  height: 38px;
  line-height: 38px;
}

.el-date-picker {
  width: 100% !important;
}

.el-date-picker >>> .el-input__inner {
  font-size: 0.875rem;
  height: 38px;
  line-height: 38px;
}

/* Botones de acción */
.btn {
  border-radius: 0.375rem;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  padding: 0.625rem 1.25rem;
  transition: all 0.3s ease;
}

.btn-fill {
  min-width: 120px;
}

.btn-outline-danger.rounded-circle {
  transition: all 0.3s ease;
}

.btn-outline-danger.rounded-circle:hover {
  background-color: #dc3545;
  color: white;
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .socio-membresia .card {
    border-radius: 0;
  }
  
  .socio-membresia .card-header {
    padding: 0.75rem 0;
    border-bottom: 1px solid #dee2e6;
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
  }
  
  .socio-membresia .card-header h5 {
    font-size: 1rem;
  }
  
  .socio-membresia .card-body {
    padding: 1rem 0;
  }
  
  .row {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }
  
  .row > div {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    margin-bottom: 1rem;
  }
  
  label {
    font-size: 0.8125rem;
    margin-bottom: 0.25rem;
  }
  
  .form-control,
  .el-select >>> .el-input__inner,
  .el-date-picker >>> .el-input__inner {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }
  
  /* Botones más grandes en móvil para mejor toque */
  .btn {
    min-height: 44px;
    font-size: 0.875rem;
  }
  
  /* Ajustes para switches en móvil */
  .switch {
    margin: 0.5rem 0;
  }
}
</style>
